'use client';

/**
 * usePatchOrgProfile — PATCH /api/v1/org/profile.
 *
 * Updates the business profile (entity_type / state / industry / GSTIN
 * etc.) that drives Compliance Health Score, Tax computations, and the
 * Compliance Calendar. Admin role required server-side.
 *
 * Empty strings clear a field (server-side maps `""` to NULL).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useApi } from '@/lib/api';

import { USERS_ME_QUERY_KEY } from './useUsersMe';

export interface OrgProfilePatch {
  entity_type?: string | null;
  business_state?: string | null;
  industry?: string | null;
  gstin?: string | null;
  cin?: string | null;
  pan?: string | null;
  employee_count?: number | null;
  turnover_bracket?: string | null;
  preferred_jurisdiction?: string | null;
  preferred_language?: string | null;
  /** Documents v1 — per-category retention in days (1..3650), keys are
   *  document categories; `null` clears every window (keep everything).
   *  A key left out of the object is "keep" for that category. */
  document_retention?: Record<string, number> | null;
  /** Migration 226 — drafts go to the client before a lawyer approves them. */
  draft_client_review_required?: boolean;
  /** Migration 274 — finalizing a generated draft needs an approver. */
  draft_finalize_requires_approval?: boolean;
}

export function usePatchOrgProfile() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<{ ok: boolean }, Error, OrgProfilePatch>({
    mutationFn: (body) =>
      api<{ ok: boolean }>('/org/profile', { method: 'PATCH', body }),
    // Returned, not fired and forgotten — react-query awaits it before the
    // caller's own `onSuccess`, so a form that drops its draft there is
    // looking at refetched data (the same shape as `usePatchProfile`).
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_ME_QUERY_KEY }),
  });
}
